import { createContext, ReactNode, use, useMemo } from "react";
import { Dayjs } from "dayjs";
import { useTenant } from "hooks/settings";
import {
  MassDataPoint,
  MassTimeSeries,
  useGetMassTimeseriesQuery,
} from "src/store/api/generatedApi";

interface MassDataContextValue {
  data: MassTimeSeries | undefined;
  isLoading: boolean;
  isError: boolean;
  filterToWindow: (start: Dayjs, end: Dayjs) => MassTimeSeries | undefined;
  queryArgs: {
    tenantName: string;
    start: string;
    end: string;
    location: string | undefined;
  };
}

export const MassDataContext = createContext<MassDataContextValue | null>(null);

// Pad the mass-data fetch so the chart can render scale traces for mass events
// that the backend matched from neighbouring periods.
const MASS_DATA_PADDING_MINUTES = 60;

type MassDataProviderProps = Readonly<{
  children: ReactNode;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  enabled?: boolean;
  location?: string;
}>;

export const MassDataProvider = ({
  children,
  windowStart,
  windowEnd,
  enabled = true,
  location,
}: MassDataProviderProps) => {
  const tenantName = useTenant();

  const startIso = useMemo(
    () =>
      windowStart.subtract(MASS_DATA_PADDING_MINUTES, "minute").toISOString(),
    [windowStart]
  );
  const endIso = useMemo(
    () => windowEnd.add(MASS_DATA_PADDING_MINUTES, "minute").toISOString(),
    [windowEnd]
  );

  const queryArgs = useMemo(
    () => ({ tenantName, start: startIso, end: endIso, location }),
    [tenantName, startIso, endIso, location]
  );

  const { data, isLoading, isError } = useGetMassTimeseriesQuery(queryArgs, {
    skip: !enabled,
  });

  const filterToWindow = useMemo(() => {
    return (start: Dayjs, end: Dayjs): MassTimeSeries | undefined => {
      if (!data) return undefined;

      const startMs = start.valueOf();
      const endMs = end.valueOf();

      const filteredSeries: MassDataPoint[] = data.series.filter((point) => {
        const pointMs = new Date(point.timestamp).getTime();
        return pointMs >= startMs && pointMs <= endMs;
      });

      return {
        start: start.toISOString(),
        end: end.toISOString(),
        series: filteredSeries,
        candidate_events: data.candidate_events,
      };
    };
  }, [data]);

  const value = useMemo(
    () => ({ data, isLoading, isError, filterToWindow, queryArgs }),
    [data, isLoading, isError, filterToWindow, queryArgs]
  );

  return <MassDataContext value={value}>{children}</MassDataContext>;
};

export const useMassData = (): MassDataContextValue => {
  const context = use(MassDataContext);
  if (!context) {
    throw new Error("useMassData must be used within a MassDataProvider");
  }
  return context;
};
